import { Component, OnDestroy, OnInit, inject } from "@angular/core";
import { CardDisplayService } from "@app/shared/card-display";
import { IColumnData, ITranslateObject, TaxprepT1Card } from "@app/views-interfaces";
import { AnalyticsService, ColumnDataLayoutService, DashboardsService, FiltersService, StepsService, WkTranslatePipe } from "@shared/index";
import { Subscription } from "rxjs";
import { debounceTime } from "rxjs/operators";
import { AuthService } from "@shared/auth/auth.service";
import { CardViewService } from "@shared/card-view-service/card-view.service";
import { CountersService } from "@shared/counters-service/counters.service";
import { HelpersService } from "@shared/helpers-service/helpers.service";
import { DragAndDropService } from "@app/drag-and-drop/drag-and-drop.service";

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: "app-completed-view",
    templateUrl: "./completed-view.component.html",
    styleUrls: ["./../views-common.scss", "./completed-view.component.scss"],
    standalone: false,
    host: { class: "wk-cv" },
    providers: [WkTranslatePipe],
})
export class CompletedViewComponent implements OnInit, OnDestroy {
    private readonly columnDataLayoutService = inject(ColumnDataLayoutService); // TODO This is injected to perform side-effects, we should refactor and remove
    private readonly analyticsService = inject(AnalyticsService);
    private readonly authService = inject(AuthService);
    private readonly cardDisplayService = inject(CardDisplayService);
    private readonly cardViewService = inject(CardViewService);
    private readonly countersService = inject(CountersService);
    private readonly dashboardsService = inject(DashboardsService);
    private readonly filtersService = inject(FiltersService);
    private readonly helpersService = inject(HelpersService);
    private readonly stepsService = inject(StepsService);
    private readonly dragAndDropService = inject(DragAndDropService);

    selectedStepId = "all_steps_completed";
    displayMode = "Kanban";
    subscription: Subscription = new Subscription();
    averageDaysInFlow = 0;
    numberOfReturn = 0;
    printing = false;
    public userCanPrintExport = false;
    isLoadingColumn = false;
    columns: IColumnData<TaxprepT1Card>[] = [];
    private rawColumns: IColumnData<TaxprepT1Card>[] = [];
    listPillInfo: {
        id: string;
        columnName: string | ITranslateObject;
    }[];

    constructor() {}

    ngOnInit() {
        this.cardViewService.setCurrentViewType("completed");
        this.analyticsService.track("Viewed Completed returns");
        this.init();

        this.subscription.add(
            this.cardViewService.viewDisplayModeChangeEvent$.subscribe(() => {
                this.displayMode = this.cardViewService.currentViewDisplayMode;
                if (this.displayMode !== "List") {
                    this.selectedStepId = "all_steps_completed";
                }
                this.initColumns();
            }),
        );

        this.subscription.add(
            this.countersService.countersUpdated$.subscribe(() => {
                this.updateNumberOfReturn();
            }),
        );
        this.subscription.add(
            this.dashboardsService.currentDashboardChanged$.subscribe(() => {
                // Synchronously invalidate stale column data to avoid filters applied to wrong card types on dashboard switch (DASH-21677)
                this.rawColumns = [];
                this.columns = [];
                this.init();
            }),
        );
        this.subscription.add(
            this.cardDisplayService.cardChangedEvent$.pipe(debounceTime(200), this.dragAndDropService.deferWhileDragging()).subscribe(() => {
                this.initColumns();
            }),
        );
        this.subscription.add(
            this.filtersService.filtersChangedEvent$.pipe(this.dragAndDropService.deferWhileDragging()).subscribe(() => {
                this.applyCardFilters();
            }),
        );
    }

    async init() {
        this.isLoadingColumn = true;
        await this.filtersService.refreshFiltersDependencies();
        const currentDashboard = await this.dashboardsService.getCurrentDashboard();
        this.userCanPrintExport = this.authService.currentUser.can_print_export === "True";
        const key = this.helpersService.getCurrentUser().sub + currentDashboard.Id + currentDashboard.TaxYear + this.cardViewService.currentViewType;
        this.displayMode = localStorage.getItem(key) ?? "Kanban";
        this.cardViewService.selectedStepId = "all_steps_completed";
        await this.initPills();
        await this.initColumns();
        this.isLoadingColumn = false;
    }

    async initPills() {
        const steps = await this.stepsService.getStepsList();
        this.listPillInfo = [
            {
                id: "all_steps_completed",
                columnName: "Dashboard.AllCompletedReturns",
            },
            ...steps
                .filter((step) => step.State === "completed")
                .map((step) => {
                    return {
                        id: step.Id,
                        columnName: step.Name,
                    };
                }),
        ];
    }

    async initColumns() {
        const cardModel = await this.cardDisplayService.getModel();
        const steps = await this.stepsService.getStepsList();
        await this.helpersService.mergeSteps(); // TODO return this thing where it came back or so help me
        const mergedSteps = this.helpersService.getMergedSteps(); // TODO return this thing where it came back or so help me

        const perStepColumns: IColumnData<TaxprepT1Card>[] =
            steps
                ?.filter((step) => step.State === "completed")
                .map((step) => {
                    const statuses = mergedSteps
                        .find((mergedStep) => mergedStep.Id === step.Id)
                        ?.Statuses.filter((status) => status.TaxReturnStatus != null);
                    const stepStatusIds = new Set<string | null | undefined>((statuses ?? []).map((status) => status.TaxReturnStatus.Id));
                    return {
                        id: step.Id,
                        columnName: step.Name,
                        statuses: statuses,
                        state: step.State,
                        isCustomStatus: false,
                        fromCache: false,
                        cards: cardModel.filter((model) => stepStatusIds.has(model.statusId)).flatMap((model) => model.cards as TaxprepT1Card[]),
                    } as IColumnData<TaxprepT1Card>;
                }) ?? [];

        if (this.displayMode === "List" && this.selectedStepId === "all_steps_completed") {
            this.rawColumns = [
                {
                    id: "all_steps_completed",
                    columnName: "Dashboard.AllCompletedReturns",
                    statuses: perStepColumns.flatMap((column) => column.statuses ?? []),
                    state: "completed",
                    isCustomStatus: false,
                    fromCache: false,
                    cards: perStepColumns.flatMap((column) => column.cards),
                },
            ];
        } else {
            this.rawColumns = perStepColumns.filter((column) => this.selectedStepId === "all_steps_completed" || column.id === this.selectedStepId);
        }
        this.applyCardFilters();
        this.updateNumberOfReturn();
    }

    private applyCardFilters(): void {
        this.columns = this.rawColumns.map((column) => {
            const cards = this.filtersService.filterCards(column.cards) as TaxprepT1Card[];
            this.filtersService.sortCards(cards);
            return { ...column, cards };
        });
    }

    ngOnDestroy() {
        this.subscription?.unsubscribe();
    }

    stepSelection(stepId: string, statusName: string | ITranslateObject) {
        this.analyticsService.track("Viewed Completed - Changed steps", { Step: typeof statusName === "string" ? statusName : statusName["en-CA"] });
        this.selectedStepId = stepId;
        this.cardViewService.selectedStepId = stepId;
        this.initColumns();
    }

    printPage() {
        if (!this.printing) {
            this.analyticsService.track("Printed Completed view");
            this.printing = true;
            setTimeout(() => {
                this.printing = false;
            }, 1000);
        }
    }

    updateNumberOfReturn() {
        // TODO now this loads too fast and we have to put a condition here
        if (this.countersService.statisticsModel) {
            const selectedModels = this.countersService.statisticsModel.filter(
                (model) => model.stepId === this.selectedStepId || (this.selectedStepId === "all_steps_completed" && model.state === "completed"),
            );
            this.numberOfReturn = selectedModels.reduce((sum, model) => sum + model.count, 0);
            this.averageDaysInFlow = Math.ceil(selectedModels.reduce((sum, model) => sum + model.totalDaysInWorkflow, 0) / this.numberOfReturn);
        }
    }
}
